import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PublicShareLayout } from "@/components/document-sharing/PublicShareLayout";

interface ShareMeta {
  id: string;
  mode: "file" | "folder_snapshot" | "folder_live_mirror";
  expiresAt: string;
  emailGateEnabled: boolean;
  requiresPin: boolean;
  message: string | null;
  createdAt: string;
  sharedBy: string | null;
  expired: boolean;
}

interface PublicShareViewerProps {
  token: string;
}

async function fetchShareMeta(token: string): Promise<ShareMeta> {
  const res = await fetch(`/api/share/${encodeURIComponent(token)}/meta`);
  if (!res.ok) throw new Error("Share not found or revoked");
  const body = (await res.json()) as { data: ShareMeta };
  return body.data;
}

export function PublicShareViewer({ token }: PublicShareViewerProps) {
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const metaQuery = useQuery({
    queryKey: ["public-share", token],
    queryFn: () => fetchShareMeta(token),
    retry: false,
  });

  const meta = metaQuery.data;

  if (metaQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Loading…</p>
      </div>
    );
  }

  if (metaQuery.isError || !meta) {
    return (
      <PublicShareLayout sharedBy={null}>
        <h1 className="text-2xl font-semibold text-gray-900">Link unavailable</h1>
        <p className="mt-2 text-gray-600">
          This share link has been disabled or could not be found.
        </p>
      </PublicShareLayout>
    );
  }

  if (meta.expired) {
    return (
      <PublicShareLayout sharedBy={meta.sharedBy}>
        <h1 className="text-2xl font-semibold text-gray-900">Link has expired</h1>
        <p className="mt-2 text-gray-600">
          Please contact{meta.sharedBy ? ` ${meta.sharedBy}` : " the sender"} for renewed access.
        </p>
      </PublicShareLayout>
    );
  }

  const needsUnlock = meta.emailGateEnabled || meta.requiresPin;

  async function handleUnlock(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setUnlockError(null);
    try {
      const res = await fetch(`/api/share/${encodeURIComponent(token)}/unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: meta?.emailGateEnabled ? email : undefined,
          pin: meta?.requiresPin ? pin : undefined,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setUnlockError(body.error ?? "Could not unlock");
        return;
      }
      setUnlocked(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (needsUnlock && !unlocked) {
    return (
      <PublicShareLayout sharedBy={meta.sharedBy}>
        {meta.message && (
          <div className="mb-6 rounded border border-gray-200 bg-white p-4 text-sm text-gray-700">
            {meta.message}
          </div>
        )}
        <form onSubmit={handleUnlock} className="space-y-4">
          {meta.emailGateEnabled && (
            <div>
              <label htmlFor="share-email" className="block text-sm font-medium text-gray-700">
                Your email
              </label>
              <input
                id="share-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          )}
          {meta.requiresPin && (
            <div>
              <label htmlFor="share-pin" className="block text-sm font-medium text-gray-700">
                Access PIN
              </label>
              <input
                id="share-pin"
                type="password"
                required
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          )}
          {unlockError && (
            <p className="text-sm text-red-600">
              {unlockError === "pin_invalid"
                ? "Incorrect PIN"
                : unlockError === "email_not_allowed"
                  ? "This email is not authorized for this share"
                  : unlockError === "expired"
                    ? "This link has expired"
                    : unlockError === "revoked"
                      ? "This link has been disabled"
                      : "Could not unlock — please try again"}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? "Unlocking…" : "View document"}
          </button>
        </form>
      </PublicShareLayout>
    );
  }

  // Unlocked (or no gate) — render the document inline.
  return (
    <PublicShareLayout sharedBy={meta.sharedBy}>
      {meta.message && (
        <div className="mb-4 rounded border border-gray-200 bg-white p-4 text-sm text-gray-700">
          {meta.message}
        </div>
      )}
      <div className="mb-4 flex items-center gap-3">
        <a
          href={`/api/share/${encodeURIComponent(token)}/file?download=1`}
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Download
        </a>
        <span className="text-xs text-gray-500">
          Expires {new Date(meta.expiresAt).toLocaleDateString()}
        </span>
      </div>
      <iframe
        title="Shared document"
        src={`/api/share/${encodeURIComponent(token)}/file`}
        className="h-[80vh] w-full rounded border border-gray-200 bg-white"
      />
    </PublicShareLayout>
  );
}
